import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import { BookingCruiseForm, CRUISE_STATUS_LABEL, CruisePassenger, addCruiseBooking } from "src/store/reducers/bookings";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import { fetchListCruiseLines } from "src/store/reducers/cruiseLines";
import CustomReactSelectWithLabel from "src/layouts/form/CustomReactSelectWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";

type IAddCruiseBookingCanvas = {
  Title: string;
  show: boolean;
  bookingId: string;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddCruiseBookingCanvas = ({
  Title,
  show,
  bookingId,
  setShow,
}: IAddCruiseBookingCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();

  const cruiseLinesList = useAppSelector((state) => state.cruiseLines.list.data);
  const [passengers, setPassengers] = useState<CruisePassenger[]>([
    { name: "", passport: "", contact: "" },
  ]);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    errors,
    setErrors,
  } = useFormik<{
    cruise_line: string;
    ship_name: string;
    departure_port: string;
    arrival_port: string;
    departure_datetime: string;
    arrival_datetime: string;
    cabin_type: string;
    deck_number: string;
    cabin_number: string;
    fare: string;
    currency: string;
    shore_excursions: string;
    advance_amount_paid: string;
    status: number;
  }>({
    initialValues: {
      cruise_line: "",
      ship_name: "",
      departure_port: "",
      arrival_port: "",
      departure_datetime: "",
      arrival_datetime: "",
      cabin_type: "",
      deck_number: "",
      cabin_number: "",
      fare: "",
      currency: "",
      shore_excursions: "",
      advance_amount_paid: "",
      status: 3,
    },
    validationSchema: Yup.object({
      cruise_line: KeyRequired("Cruise line"),
      departure_datetime: KeyRequired("Departure date time"),
      arrival_datetime: KeyRequired("Arrival date time"),
      fare: Yup.number()
        .required("Fare is required")
        .typeError("Fare must be a number")
        .positive("Fare is required"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      addU({ ...values, passengers, booking: bookingId });
    },
  });

  const addU = useCallback(
    async (values: BookingCruiseForm) => {
      try {
        await dispatch(addCruiseBooking(values)).unwrap();
        toast.success("Booking cruise is added successfully");
        setShow(false);
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, setErrors, setShow]
  );

  useEffect(() => {
    resetForm();
    setPassengers([{ name: "", passport: "", contact: "" }]);
    if (show) {
      dispatch(fetchListCruiseLines());
    }
  }, [dispatch, resetForm, show]);

  const addPassenger = () => {
    setPassengers([...passengers, { name: "", passport: "", contact: "" }]);
  };

  const removePassenger = (index: number) => {
    setPassengers(passengers.filter((_, i) => i !== index));
  };

  const updatePassenger = (index: number, field: keyof CruisePassenger, value: string) => {
    const updated = [...passengers];
    updated[index] = { ...updated[index], [field]: value };
    setPassengers(updated);
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => setShow(false)}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button
          type="button"
          className="btn-close"
          onClick={() => setShow(false)}
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomReactSelectWithLabel
                  labelText="Cruise Line"
                  placeholder="Cruise Line"
                  id={"cruise_line"}
                  required
                  options={cruiseLinesList.map((cl) => ({
                    label: cl.cruise_line_name,
                    value: cl.id,
                  }))}
                  touched={touched.cruise_line}
                  errors={errors.cruise_line}
                  handleChange={handleChange}
                  value={
                    cruiseLinesList
                      .filter((cl) => cl.id === values.cruise_line)
                      ?.map((cl) => ({
                        label: cl.cruise_line_name,
                        value: cl.id,
                      }))?.[0] || ""
                  }
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="ship_name"
                  labelText="Ship Name"
                  handleChange={handleChange}
                  value={values.ship_name}
                  touched={touched.ship_name}
                  errors={errors.ship_name}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="departure_port"
                  labelText="Departure Port"
                  handleChange={handleChange}
                  value={values.departure_port}
                  touched={touched.departure_port}
                  errors={errors.departure_port}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="arrival_port"
                  labelText="Arrival Port"
                  handleChange={handleChange}
                  value={values.arrival_port}
                  touched={touched.arrival_port}
                  errors={errors.arrival_port}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="departure_datetime"
                  labelText="Departure Date & Time"
                  required={true}
                  pickerType="datetime"
                  handleChange={(date) => setFieldValue("departure_datetime", date)}
                  value={values.departure_datetime}
                  selectsStart
                  endDate={values.arrival_datetime}
                  touched={touched.departure_datetime}
                  errors={errors.departure_datetime}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="arrival_datetime"
                  labelText="Arrival Date & Time"
                  required={true}
                  pickerType="datetime"
                  handleChange={(date) => setFieldValue("arrival_datetime", date)}
                  value={values.arrival_datetime}
                  selectsEnd
                  startDate={values.departure_datetime}
                  touched={touched.arrival_datetime}
                  errors={errors.arrival_datetime}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="cabin_type"
                  labelText="Cabin Type"
                  handleChange={handleChange}
                  value={values.cabin_type}
                  touched={touched.cabin_type}
                  errors={errors.cabin_type}
                />
              </div>
              <div className="col-xl-3 mb-3">
                <CustomInputWithLabel
                  id="deck_number"
                  labelText="Deck Number"
                  handleChange={handleChange}
                  value={values.deck_number}
                  touched={touched.deck_number}
                  errors={errors.deck_number}
                />
              </div>
              <div className="col-xl-3 mb-3">
                <CustomInputWithLabel
                  id="cabin_number"
                  labelText="Cabin Number"
                  handleChange={handleChange}
                  value={values.cabin_number}
                  touched={touched.cabin_number}
                  errors={errors.cabin_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="fare"
                  labelText="Fare"
                  required={true}
                  handleChange={handleChange}
                  value={values.fare}
                  touched={touched.fare}
                  errors={errors.fare}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="currency"
                  labelText="Currency"
                  handleChange={handleChange}
                  value={values.currency}
                  touched={touched.currency}
                  errors={errors.currency}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="advance_amount_paid"
                  labelText="Advance Amount Paid"
                  handleChange={handleChange}
                  value={values.advance_amount_paid}
                  touched={touched.advance_amount_paid}
                  errors={errors.advance_amount_paid}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="status"
                  labelText="Status"
                  handleChange={handleChange}
                  value={values.status}
                  touched={touched.status}
                  errors={errors.status}
                >
                  {Object.entries(CRUISE_STATUS_LABEL).map(([key, label]) => (
                      <option value={key} key={key}>
                        {label}
                      </option>
                    ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="shore_excursions"
                  labelText="Shore Excursions"
                  handleChange={handleChange}
                  value={values.shore_excursions}
                  touched={touched.shore_excursions}
                  errors={errors.shore_excursions}
                />
              </div>

              <div className="col-xl-12 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Passengers</Form.Label>
                  <button
                    type="button"
                    className="btn btn-primary btn-xs"
                    onClick={addPassenger}
                  >
                    + Add Passenger
                  </button>
                </div>
                {passengers.map((passenger, index) => (
                  <div className="row mb-2 align-items-end" key={index}>
                    <div className="col-xl-4">
                      <CustomInputWithLabel
                        id={`passenger_name_${index}`}
                        placeholder="Name"
                        handleChange={(e: any) =>
                          updatePassenger(index, "name", e.target.value)
                        }
                        value={passenger.name}
                      />
                    </div>
                    <div className="col-xl-3">
                      <CustomInputWithLabel
                        id={`passenger_passport_${index}`}
                        placeholder="Passport"
                        handleChange={(e: any) =>
                          updatePassenger(index, "passport", e.target.value)
                        }
                        value={passenger.passport}
                      />
                    </div>
                    <div className="col-xl-3">
                      <CustomInputWithLabel
                        id={`passenger_contact_${index}`}
                        placeholder="Contact"
                        handleChange={(e: any) =>
                          updatePassenger(index, "contact", e.target.value)
                        }
                        value={passenger.contact}
                      />
                    </div>
                    <div className="col-xl-2">
                      {passengers.length > 1 ? (
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removePassenger(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              Submit
            </button>
            <Link
              to={"#"}
              onClick={() => setShow(false)}
              className="btn btn-danger light ms-1"
            >
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default AddCruiseBookingCanvas;
